/**
 * Steg 1 i sangimporten: velg sangfiler (én fil per sang) eller ett regneark (mange sanger).
 * Ren visning: valgt fil/filer sendes opp til veiviseren, som gjør selve lesingen.
 */
import React, { useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const SANGFILENDELSER = '.cho,.chopro,.crd,.pro,.txt,.pdf';
const REGNEARKENDELSER = '.xlsx,.csv';

interface Steg1ValgProps {
  onVelgSangfiler: (filer: File[]) => void;
  onVelgRegneark: (fil: File) => void;
  onLastNedMal: () => void;
  feil?: string | null;
  opptatt?: boolean;
  /** Elementet stegtittelen vises i; veiviseren sender dialogens DialogTitle. */
  Tittel?: React.ElementType;
}

interface ValgKortProps {
  tittel: string;
  beskrivelse: string;
  knappetekst: string;
  opptatt: boolean;
  onKlikk: () => void;
}

const ValgKort: React.FC<ValgKortProps> = ({ tittel, beskrivelse, knappetekst, opptatt, onKlikk }) => (
  <Card data-testid="steg1-valgkort" className="flex h-full min-w-0 flex-col">
    <CardHeader className="space-y-1.5 p-4">
      <CardTitle className="text-base">{tittel}</CardTitle>
      <CardDescription>{beskrivelse}</CardDescription>
    </CardHeader>
    <CardContent className="mt-auto p-4 pt-0">
      <div data-testid="steg1-knapperad" className="mt-auto">
        <Button type="button" variant="outline" disabled={opptatt} onClick={onKlikk}>
          {knappetekst}
        </Button>
      </div>
    </CardContent>
  </Card>
);

const Steg1Valg: React.FC<Steg1ValgProps> = ({ onVelgSangfiler, onVelgRegneark, onLastNedMal, feil, opptatt = false, Tittel = 'h2' }) => {
  const sangRef = useRef<HTMLInputElement>(null);
  const arkRef = useRef<HTMLInputElement>(null);

  const handleSangfiler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const filer = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (filer.length > 0) onVelgSangfiler(filer);
  };

  const handleRegneark = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fil = e.target.files?.[0];
    e.target.value = '';
    if (fil) onVelgRegneark(fil);
  };

  return (
    <div className="min-w-0 space-y-4">
      <div className="space-y-1">
        <p className="text-sm text-muted-foreground">Steg 1 av 3</p>
        <Tittel
          tabIndex={-1}
          data-steg-tittel=""
          className="text-lg font-semibold leading-none tracking-tight focus:outline-none"
        >
          Importer sanger
        </Tittel>
      </div>

      <div data-testid="steg1-kort" className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <ValgKort
          tittel="Én fil per sang"
          beskrivelse="PDF, ChordPro eller tekstfil med tekst og akkorder."
          knappetekst="Velg sangfiler"
          opptatt={opptatt}
          onKlikk={() => sangRef.current?.click()}
        />
        <ValgKort
          tittel="Mange sanger i ett regneark"
          beskrivelse="Excel eller CSV med én sang per rad, også lenker og kategorier."
          knappetekst="Velg regneark"
          opptatt={opptatt}
          onKlikk={() => arkRef.current?.click()}
        />
      </div>

      <input
        ref={sangRef}
        data-testid="steg1-sangfiler"
        type="file"
        multiple
        accept={SANGFILENDELSER}
        className="hidden"
        tabIndex={-1}
        aria-hidden="true"
        onChange={handleSangfiler}
      />
      <input
        ref={arkRef}
        data-testid="steg1-regneark"
        type="file"
        accept={REGNEARKENDELSER}
        className="hidden"
        tabIndex={-1}
        aria-hidden="true"
        onChange={handleRegneark}
      />

      <div className="flex flex-wrap items-center gap-x-2 text-sm">
        <span>Skal du bruke regneark?</span>
        <Button type="button" variant="link" className="h-auto p-0" disabled={opptatt} onClick={onLastNedMal}>
          Last ned mal
        </Button>
        {opptatt && <Loader2 className="h-4 w-4 animate-spin" role="status" aria-label="Laster" />}
      </div>

      {feil && (
        <p role="alert" className="text-sm text-destructive">
          {feil}
        </p>
      )}
    </div>
  );
};

export default Steg1Valg;
